import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import RichTextConverter from '../RichText/RichTextConverter';
import { smoothScrollToOffset } from '../ScrollTo';
import ScrollTo from '../ScrollTo';
import AdTest from './AdTest';
import FormContainer from './FormContainer';
import * as styles from './MarketoForm.module.scss';
import { handleDemoButton, scriptLoaded } from './marketoFormEffects';
import { submitSuccessEvents } from './submitForm';

const MarketoForm = ({ formId, followUpForms, onSuccess, redirectUrl, onFormLoaded, ...rest }) => {
  const [step, setStep] = useState(0);
  const topRef = useRef(null);
  if (!followUpForms) {
    return (
      <MarketoFormContainer
        formId={formId}
        onSuccess={onSuccess}
        onFormLoaded={onFormLoaded}
        redirectUrl={redirectUrl}
        {...rest}
      />
    );
  }
  let forms = followUpForms.split(',');
  forms.unshift(formId);

  const finalStep = forms.length - 1;
  const onStepSuccess = () => {
    if (step === finalStep) {
      smoothScrollToOffset(topRef.current, 120);
    } else {
      setStep(step + 1);
      smoothScrollToOffset(topRef.current, 120);
    }
  };

  let components = forms.map((id, i) => {
    return (
      <MarketoFormContainer
        isNewDemo={rest.isNewDemo}
        key={i}
        formId={id}
        onSuccess={i === finalStep ? onSuccess : onStepSuccess}
        onFormLoaded={onFormLoaded}
        {...rest}
      />
    );
  });
  return (
    <div key={`current-step-${step}`}>
      <ScrollTo reference={topRef} />
      <div className={`current-step-${step}`} />
      {components[step]}
    </div>
  );
};

const MarketoFormContainer = ({
  formId,
  onSuccess,
  onFormLoaded,
  redirectUrl,
  className,
  successMessage,
  trackingPixelUrl,
  isNewDemo,
  addHiddenFields,
  adCheckClass,
  productOfInterest,
}) => {
  const [success, setSuccess] = useState(false);
  const [formLoaded, setFormLoaded] = useState(false);
  const [formUnavailable, setFormUnavailable] = useState(false);
  const isMounted = useRef(false);
  const intervalId = useRef(null);
  const handleSuccess = async (values) => {
    submitSuccessEvents({ formId, values });
    if (redirectUrl) {
      window.location = redirectUrl;

      return false;
    }
    if (isMounted.current) setSuccess(true);
    if (onSuccess) await onSuccess();
    return false;
  };

  useEffect(() => {
    isMounted.current = true;

    if (!formLoaded) {
      scriptLoaded({
        formId,
        formLoaded,
        setFormLoaded,
        setFormUnavailable,
        isMounted,
        intervalId,
        handleSuccess,
        onFormLoaded,
        addHiddenFields,
        productOfInterest,
      });
    }

    handleDemoButton(productOfInterest);

    return () => {
      isMounted.current = false;
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
    };
    // eslint-disable-next-line
  }, []);

  if (formUnavailable) {
    return <AdTest formId={formId} formLoadFailed={formUnavailable} className={adCheckClass} />;
  }

  if (success) {
    const defaultMessage = <span>Thank you! A team member will follow up within one business day.</span>;
    return (
      <div
        data-analytics-section={'form-success'}
        className={[className, styles.marketoFormSuccess, 'marketo-render-success'].join(' ')}
      >
        {successMessage && successMessage.raw ? (
          <RichTextConverter copy={successMessage} />
        ) : successMessage ? (
          <span>{successMessage}</span>
        ) : (
          defaultMessage
        )}
        {trackingPixelUrl && (
          <img className={'li-tr'} src={trackingPixelUrl} height="1" width="1" style={{ display: 'none' }} alt="" />
        )}
      </div>
    );
  }

  const fade = formLoaded ? styles.fadeIn : styles.fadeOut;
  return (
    <>
      {!formLoaded && !success && <span key="loading-spinner" className={styles.spinner} />}
      <FormContainer className={className} fade={fade} isNewDemo={isNewDemo} formId={formId} />
    </>
  );
};

MarketoFormContainer.propTypes = {
  formId: PropTypes.string.isRequired,
  redirectUrl: PropTypes.string,
  className: PropTypes.string,
  successTitle: PropTypes.string,
  successImage: PropTypes.string,
  successMessage: PropTypes.any,
  onSuccess: PropTypes.func,
  addHiddenFields: PropTypes.bool,
  adCheckClass: PropTypes.string,
  onFormLoaded: PropTypes.func,
  productOfInterest: PropTypes.string,
  trackingPixelUrl: PropTypes.string,
  isNewDemo: PropTypes.bool,
};

MarketoForm.propTypes = {
  formId: PropTypes.string.isRequired,
  redirectUrl: PropTypes.string,
  className: PropTypes.string,
  successTitle: PropTypes.string,
  successImage: PropTypes.string,
  successMessage: PropTypes.any,
  onSuccess: PropTypes.func,
  addHiddenFields: PropTypes.bool,
  adCheckClass: PropTypes.string,
  onFormLoaded: PropTypes.func,
};

export default MarketoForm;
